/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { TableBody, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import SimCardInterface from '../../interfaces/platform/SimCardInterface';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { useDispatch, useSelector } from 'react-redux';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import PaperedTable from '../../components/Table/PaperedTable';
import TableCell from '@material-ui/core/TableCell';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import { HeaderCheckBoxSelectListElement } from '../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { provConstant, ProvReducerType } from '../../modules/provisioning/ProvisioningConnect';
import { FupHistoryCollection } from '../../interfaces/platform/FupHistoryInterface';
import Pager from '../../components/Adescom/PagerRange';
import { MnoMessageCollectionInterface } from '../../interfaces/collections/MnoMessageInterface';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import { SimCardOperationHelper } from '../../helpers/SimCardOperationHelper';
import I18n from '../../components/Translate/I18n';
import { FilterManager } from '../../modules/filter/FilterManager';

type FupHistoryListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const FupHistoryList: FunctionComponent<FupHistoryListType> = ({ classes, showMainLoader }): ReactElement => {
    const dispatch = useDispatch();
    const setDefaultFilters = (): Array<Filter> => [];
    const setDefaultOrders = (): Array<Order> => [{ name: 'createdAt', value: 'desc' }];
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [activeRow, setActiveRow] = useState(-1);
    const [fupHistories, setFupHistories] = useState<FupHistoryCollection | null>(null);
    const [reload, setReload] = useState(false);

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        setFupHistories(null);
    };

    const fupStatus = (): Array<HeaderCheckBoxSelectListElement> => {
        return [
            { name: 'simcard:list.red', value: 3 },
            { name: 'simcard:list.green', value: 1 },
            { name: 'simcard:list.yellow', value: 2 },
        ];
    };

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    useEffect(() => {
        if (fupHistories === null || reload) {
            const processFilters = (): Array<Filter> => {
                const tmp: Array<Filter> = [];
                for (let i = 0; i < filters.length; i++) {
                    if (filters[i].name === 'fupState') {
                        const values = filters[i].value.toString().split(',');
                        for (let j = 0; j < values.length; j++) {
                            tmp.push({ name: 'fupState[]', value: values[j] });
                        }
                    } else {
                        tmp.push(filters[i]);
                    }
                }
                return tmp;
            };
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fup_histories')),
                'FUP_HISTORIES_GET',
                processFilters(),
                orders,
                (data: FupHistoryCollection) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    setFupHistories(data);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
            setReload(false);
        }
    }, [dispatch, filters, orders, fupHistories, showMainLoader, reload]);

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'FUP_HISTORIES_GET',
            (data: FupHistoryCollection): void => {
                setFupHistories(data);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}></GridItem>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                            </GridItem>
                            <FilterList />
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="simcard:list.msisdn"
                                    fieldName="msisdn"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    prependMaxLengthRequirement={9}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="simcard:f.fup_event_date"
                                    fieldName="createdAt"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="simcard:f.fup_event_count"
                                    fieldName="cdrCount"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    prependMaxLengthRequirement={9}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="simcard:list.fup_status"
                                    fieldName="fupState"
                                    showSort={true}
                                    onlyFilter={false}
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    defaultDirection="desc"
                                    elementList={fupStatus()}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fupHistories &&
                            fupHistories['hydra:member'].map((fupHistory) => {
                                return (
                                    <TableRow key={'fupHistory_' + fupHistory.id}>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {fupHistory.msisdn}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {DateHelperTs.format(fupHistory.createdAt, 'YYYY-MM-DD')}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {fupHistory.cdrCount}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            <I18n trans={SimCardOperationHelper.getSimCardFupStatus(fupHistory)} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {fupHistories && fupHistories['hydra:view'] && <Pager callback={paginationCallbackFunction} views={fupHistories} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(FupHistoryList);
